/**
 * Contexte dev uniquement : override du rôle utilisé pour charger les boutons du dashboard.
 * Permet de tester la disposition des boutons par rôle sans changer de compte.
 */
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

const STORAGE_KEY = 'luxori_dev_role_override';

interface DevRoleDashboardContextType {
  /** ID du rôle utilisé pour le dashboard (override). null = utiliser le rôle réel de l'utilisateur. */
  devRoleIdOverride: string | null;
  setDevRoleIdOverride: (roleId: string | null) => void;
}

const DevRoleDashboardContext = createContext<DevRoleDashboardContextType | undefined>(undefined);

function readStored(): string | null {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return null;
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v || null;
  } catch {
    return null;
  }
}

function writeStored(roleId: string | null) {
  if (typeof window === 'undefined' || !import.meta.env.DEV) return;
  try {
    if (roleId) localStorage.setItem(STORAGE_KEY, roleId);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

export function DevRoleDashboardProvider({ children }: { children: ReactNode }) {
  const [devRoleIdOverride, setState] = useState<string | null>(readStored);

  const setDevRoleIdOverride = useCallback((roleId: string | null) => {
    setState(roleId);
    writeStored(roleId);
  }, []);

  const value = useMemo(
    () => ({ devRoleIdOverride, setDevRoleIdOverride }),
    [devRoleIdOverride, setDevRoleIdOverride]
  );

  return (
    <DevRoleDashboardContext.Provider value={value}>{children}</DevRoleDashboardContext.Provider>
  );
}

export function useDevRoleDashboard(): DevRoleDashboardContextType | undefined {
  return useContext(DevRoleDashboardContext);
}

/** Hook pour la page d'accueil : retourne le role_id à utiliser pour charger les boutons dashboard (override en dev ou rôle réel). */
export function useEffectiveRoleIdForDashboard(realRoleId: string | null): string | null {
  const ctx = useDevRoleDashboard();
  if (!import.meta.env.DEV || !ctx) return realRoleId;
  return ctx.devRoleIdOverride ?? realRoleId;
}
